// THE app-wide back-navigation affordance. One component, one look, one
// behavior, everywhere a user opens a source/record and needs to get back:
//   - "floating" variant: a fixed pill at the top of the destination view,
//     used when navigating to a record UNMOUNTED the origin (cross-tab jumps —
//     Overview / Files / Timeline / Interrogate deep, and Financials' "Open
//     full record"). Clicking it returns to the exact origin.
//   - "inline" variant: a bar inside an in-place source drawer/panel (the
//     Interrogate / Financials source view), where nothing unmounted and
//     "back" just closes the panel.
//
// Both entry-point components (BackToSummaryBar, FocusedSourceBackBar) delegate
// here, so there is a single source of truth for the visible control. Authored
// with React.createElement (not JSX) so the affordance's presence + click
// wiring are directly unit-testable under `node --test` (which strips TS types
// but not JSX). The recurring "no way back" bug shipped because tests asserted
// internal state, never the rendered control — this component is that control.

import { createElement } from "react";

export type BackBarVariant = "floating" | "inline";

interface Props {
  // The visible button text, e.g. "← Back to Overview". Callers own the label
  // so the destination always names where "back" goes.
  label: string;
  // Optional secondary line (hidden on narrow screens) clarifying context.
  hint?: string;
  onBack: () => void;
  variant?: BackBarVariant;
}

export default function BackBar({
  label,
  hint,
  onBack,
  variant = "floating",
}: Props) {
  return createElement(
    "div",
    { className: `backbar backbar-${variant}`, role: "navigation" },
    createElement(
      "button",
      { type: "button", className: "backbar-btn", onClick: onBack },
      label,
    ),
    hint ? createElement("span", { className: "backbar-hint" }, hint) : null,
  );
}
